import axios from 'axios'
import { pendingWrites, frozen } from '@/pwa/runtime'
import { putRead, getRead } from '@/pwa/drafts'
const settle = config => { if (config?.__pwaWrite) { config.__pwaWrite = false; pendingWrites.value = Math.max(0, pendingWrites.value - 1) } }
import router from '@/router'
import { i18n } from '@/i18n'

let lastStaleEmit = 0

// F13.3: GETs the offline-capable screens (tasks, feed walk-round, pen alerts)
// need to open without a connection. The last good answer is kept per account
// and served, marked with header x-sf-offline-cache, when the network fails.
const OFFLINE_READS = ['/daily-tasks', '/feeding/assessments/board', '/feeding/card-values', '/pen-alerts/summary']
const readKey = (config) => config?.method === 'get' && OFFLINE_READS.includes(config.url) ? `${config.url}?${JSON.stringify(config.params || {})}` : null

const api = axios.create({
  baseURL: import.meta.env.VITE_API_BASE_URL || '/api/v1',
  timeout: 30000,
  headers: {
    'Content-Type': 'application/json'
  }
})

// ---------------------------------------------------------------------------
// Session: access token lives 15 min. Refresh it (single-flight in this tab,
// serialized across tabs with Web Locks) instead of kicking the user out.
// The BE rotates the refresh token (old one is revoked on use), so only ONE
// refresh may use a given refresh token - hence the lock + re-read of
// localStorage before calling /auth/refresh. Tokens are never logged.
// ---------------------------------------------------------------------------
const REFRESH_AHEAD_MS = 60 * 1000
const LOCK_NAME = 'smartfarm-auth-refresh'
let refreshInflight = null
let refreshTimer = null
let sessionWired = false

const isMockToken = (t) => !t || t === 'mock-token' || t === 'mock-refresh'

export function tokenExpiryMs(token) {
  try {
    const part = String(token || '').split('.')[1]
    if (!part) return null
    const json = atob(part.replace(/-/g, '+').replace(/_/g, '/').padEnd(Math.ceil(part.length / 4) * 4, '='))
    const exp = JSON.parse(json).exp
    return typeof exp === 'number' ? exp * 1000 : null
  } catch { return null }
}

const expiresWithin = (token, ms) => {
  const exp = tokenExpiryMs(token)
  return exp !== null && exp - Date.now() <= ms
}

const isOffline = () => typeof navigator !== 'undefined' && navigator.onLine === false

async function callRefreshEndpoint(refreshToken) {
  // Plain axios (no interceptors): cannot recurse into the 401 handler.
  try {
    const res = await axios.post((api.defaults.baseURL || '') + '/auth/refresh', { refresh_token: refreshToken }, { timeout: 15000, headers: { 'Content-Type': 'application/json' } })
    const tokens = res.data?.data?.tokens
    if (!tokens?.access_token) return 'transient'
    localStorage.setItem('access_token', tokens.access_token)
    if (tokens.refresh_token) localStorage.setItem('refresh_token', tokens.refresh_token)
    return 'ok'
  } catch (err) {
    const status = err.response?.status
    return status === 400 || status === 401 || status === 403 || status === 422 ? 'invalid' : 'transient'
  }
}

// Runs inside the cross-tab lock: re-read storage first, another tab may have
// refreshed while we waited.
async function doRefresh(staleAccess) {
  const cur = localStorage.getItem('access_token')
  if (isMockToken(cur)) return 'skip'
  if (staleAccess) {
    if (cur && cur !== staleAccess && !expiresWithin(cur, 0)) return 'ok'
  } else if (!expiresWithin(cur, REFRESH_AHEAD_MS + 5000)) {
    return 'ok'
  }
  const rt = localStorage.getItem('refresh_token')
  if (isMockToken(rt)) return 'invalid'
  return callRefreshEndpoint(rt)
}

/** Refresh the session once (shared by concurrent callers). Resolves 'ok' | 'invalid' | 'transient' | 'skip'. */
export function refreshSession(staleAccess) {
  if (refreshInflight) return refreshInflight
  if (isOffline()) return Promise.resolve('transient')
  refreshInflight = (async () => {
    try {
      if (typeof navigator !== 'undefined' && navigator.locks?.request) {
        return await navigator.locks.request(LOCK_NAME, () => doRefresh(staleAccess))
      }
      return await doRefresh(staleAccess)
    } catch { return 'transient' } finally { refreshInflight = null }
  })()
  return refreshInflight
}

export function cancelTokenRefresh() {
  if (refreshTimer) { clearTimeout(refreshTimer); refreshTimer = null }
}

/** (Re)arm the single timer that refreshes ~60 s before the access token expires. */
export function scheduleTokenRefresh() {
  cancelTokenRefresh()
  const token = localStorage.getItem('access_token')
  if (isMockToken(token)) return
  const exp = tokenExpiryMs(token)
  if (exp === null) return
  const delay = Math.max(exp - REFRESH_AHEAD_MS - Date.now(), 0)
  refreshTimer = setTimeout(() => { refreshTimer = null; refreshIfDue() }, Math.min(delay, 2147483000))
}

/** Refresh now if the token is expired / about to expire (never while offline), then re-arm. */
export async function refreshIfDue() {
  const token = localStorage.getItem('access_token')
  if (isMockToken(token) || !localStorage.getItem('refresh_token')) return
  if (!expiresWithin(token, REFRESH_AHEAD_MS)) { scheduleTokenRefresh(); return }
  if (isOffline()) return // retried by the 'online' listener
  const r = await refreshSession()
  if (r === 'ok') scheduleTokenRefresh()
  else if (r === 'transient') { cancelTokenRefresh(); refreshTimer = setTimeout(() => { refreshTimer = null; refreshIfDue() }, 30000) }
  // 'invalid': leave it; the next real request gets a 401 and logs out.
}

/** Wire tab-visible / online / cross-tab storage listeners once. */
export function initSessionRefresh() {
  if (sessionWired || typeof window === 'undefined') return
  sessionWired = true
  document.addEventListener('visibilitychange', () => { if (document.visibilityState === 'visible') refreshIfDue() })
  window.addEventListener('online', () => { refreshIfDue() })
  window.addEventListener('storage', (e) => {
    if (e.key === 'access_token' || e.key === null) { if (localStorage.getItem('access_token')) scheduleTokenRefresh(); else cancelTokenRefresh() }
  })
  refreshIfDue()
}

// Request interceptor — attach the JWT bearer token issued by /auth/login.
api.interceptors.request.use(
  (config) => {
    if (frozen.value && !['get','head','options'].includes(config.method)) throw Error('Đang chuẩn bị cập nhật. Vui lòng thử lại sau.')
    // F13.4: no write (approvals and other critical actions included) is attempted
    // offline; the few offline-capable actions go through the outbox (src/pwa/outbox.js).
    if (!['get','head','options'].includes(config.method) && isOffline()) {
      const e = Error(i18n.global.t('pwa.needOnline'))
      e.code = 'SF_OFFLINE'
      throw e
    }
    if (!['get','head','options'].includes(config.method)) { config.__pwaWrite = true; pendingWrites.value++ }
    const token = localStorage.getItem('access_token')
    if (token) {
      config.headers.Authorization = `Bearer ${token}`
    }
    return config
  },
  (error) => Promise.reject(error)
)

// Response interceptor — unwrap the backend's { success, message, data, errors } envelope
// errors and force a redirect to /login on an expired/invalid session.
api.interceptors.response.use(
  (response) => {
    settle(response.config)
    const key = readKey(response.config)
    if (key) putRead(key, response.data)
    return response
  },
  async (error) => {
    settle(error.config)
    const cacheKey = !error.response && readKey(error.config)
    if (cacheKey) {
      const hit = await getRead(cacheKey)
      if (hit) return { data: hit.data, status: 200, statusText: 'OK', headers: { 'x-sf-offline-cache': hit.at }, config: error.config }
    }
    // Rate limited (bulk edits like generating dozens of pens): wait and retry
    // instead of failing half-way through.
    const cfg = error.config
    if (error.response?.status === 429 && cfg && (cfg.method === 'get' || cfg.headers?.['Idempotency-Key']) && (cfg.__retries || 0) < 4) {
      cfg.__retries = (cfg.__retries || 0) + 1
      await new Promise((resolve) => setTimeout(resolve, 1500 * cfg.__retries))
      return api(cfg)
    }
    if (error.response?.status === 401) {
      const url = error.config?.url || ''
      const isAuthEndpoint = url.includes('/auth/login') || url.includes('/auth/refresh')
      // Expired access token: refresh once and replay the original request
      // (same headers incl. Idempotency-Key, same body). A 401 means it never
      // reached business logic, so the replay cannot duplicate a write.
      if (!isAuthEndpoint && cfg && !cfg.__authRetried && localStorage.getItem('refresh_token')) {
        const auth = cfg.headers?.Authorization || (typeof cfg.headers?.get === 'function' ? cfg.headers.get('Authorization') : '')
        const sent = String(auth || '').replace(/^Bearer /, '')
        const result = await refreshSession(sent || undefined)
        if (result === 'ok') {
          cfg.__authRetried = true
          scheduleTokenRefresh()
          return api(cfg)
        }
        // offline / server hiccup: keep the session and the drafts
        if (result === 'transient') return Promise.reject(error)
      }
      const currentRoute = router.currentRoute.value
      const routeRequiresAuth = currentRoute.matched?.some((record) => record.meta?.requiresAuth)

      if (!isAuthEndpoint && routeRequiresAuth) {
        cancelTokenRefresh()
        localStorage.removeItem('access_token')
        localStorage.removeItem('refresh_token')
        localStorage.removeItem('user')
        if (currentRoute.path !== '/login') {
          sessionStorage.setItem('login_redirect', currentRoute.fullPath)
          router.push('/login')
        }
      }
    }

    // Prefer a translation keyed by the backend's stable error `code` (see
    // pkg/apperror) when one is registered — covers the handful of error
    // paths we've deliberately localized (apiErrors.* in i18n/locales).
    // Everything else still uses the backend's own message/errors as-is:
    // most of those are already plain English today, and guessing a
    // generic translation for an unmapped code would only lose detail.
    if (!error.response) {
      if (error.code === 'ERR_NETWORK') error.message = i18n.global.t('apiErrors.NETWORK_ERROR')
      else if (error.code === 'ECONNABORTED' || error.code === 'ETIMEDOUT') error.message = i18n.global.t('apiErrors.TIMEOUT_ERROR')
    }
    const status = error.response?.status
    const code = error.response?.data?.code
    const key = code ? `apiErrors.${code}` : null
    if (key && i18n.global.te(key)) {
      error.message = i18n.global.t(key, error.response.data.errors || {})
    } else if (status) {
      const backendMessage = error.response?.data?.message || error.response?.data?.errors
      const text = typeof backendMessage === 'string' ? backendMessage : (backendMessage ? JSON.stringify(backendMessage) : '')
      // Keep the server text only when it is already Vietnamese; otherwise use
      // a status-based Vietnamese message (keep detail for 409/422 validation).
      const viText = /[À-ỹ]/.test(text)
      const fallbackKey = status >= 500 ? 'apiErrors.HTTP_5XX' : `apiErrors.HTTP_${status}`
      if (text && viText) error.message = text
      else if (i18n.global.te(fallbackKey)) {
        const base = i18n.global.t(fallbackKey)
        error.message = text && (status === 409 || status === 422) ? `${base}: ${text}` : base
      } else if (text) error.message = text
    }

    // 403: the cached permissions may be stale. Ask the auth store to re-read
    // /me (debounced: at most once per 30 s, never for /me itself).
    if (status === 403 && !(error.config?.url || '').includes('/me')) {
      const now = Date.now()
      if (now - lastStaleEmit > 30000) {
        lastStaleEmit = now
        try { window.dispatchEvent(new Event('sf:permissions-stale')) } catch { /* ignore */ }
      }
    }

    return Promise.reject(error)
  }
)

export default api
